import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { FaInfoCircle } from "react-icons/fa";
import { FiEye } from "react-icons/fi";
import { toast } from "sonner";
import { db } from "../../../firebase";
import { useAuth } from "../../../contexts/AuthContext";
import MaintenancePageShell from "../MaintenancePageShell";
import {
    ContentGrid, EditorPanel, FieldGroup, Hint, Input, Label, PreviewHeader, PreviewPanel, Textarea,
} from "../shared";

const toDatetimeLocal = (date) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export default function MaintenanceBannerPage() {
    const { user, isAdmin } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isProjectManager, setIsProjectManager] = useState(false);
    const [title, setTitle] = useState("Scheduled Maintenance");
    const [message, setMessage] = useState("");
    const [startAt, setStartAt] = useState("");
    const [endAt, setEndAt] = useState("");

    const homeLink = isAdmin ? "/admin/home" : "/student/home";

    useEffect(() => {
        const loadPage = async () => {
            if (!user?.email) return;
            setLoading(true);
            try {
                const permissionSnap = await getDoc(doc(db, "Users", user.email));
                const hasAccess = permissionSnap.exists() && permissionSnap.data()?.isProjectManager === true;
                setIsProjectManager(hasAccess);
                if (!hasAccess) return;

                const snap = await getDoc(doc(db, "siteConfig", "maintenance"));
                if (snap.exists()) {
                    const data = snap.data();
                    setTitle(data.title || "Scheduled Maintenance");
                    setMessage(data.message || "");
                    if (data.startAt) {
                        const date = data.startAt.toDate ? data.startAt.toDate() : new Date(data.startAt);
                        setStartAt(toDatetimeLocal(date));
                    }
                    if (data.endAt) {
                        const date = data.endAt.toDate ? data.endAt.toDate() : new Date(data.endAt);
                        setEndAt(toDatetimeLocal(date));
                    }
                }
            } catch (error) {
                console.error("Failed to load maintenance banner:", error);
                toast.error("Failed to load banner settings.");
            } finally {
                setLoading(false);
            }
        };
        loadPage();
    }, [user]);

    const executeSave = async (publish) => {
        setSaving(true);
        try {
            await setDoc(doc(db, "siteConfig", "maintenance"), {
                enabled: publish,
                title: title.trim(),
                message: message.trim(),
                startAt: startAt ? new Date(startAt) : null,
                endAt: endAt ? new Date(endAt) : null,
                updatedAt: serverTimestamp(),
                updatedBy: user.email,
            });
            toast.success(publish ? "Maintenance banner published." : "Draft saved.");
        } catch (error) {
            console.error("Failed to save maintenance banner:", error);
            toast.error("Failed to save banner.");
        } finally {
            setSaving(false);
        }
    };

    const handleSave = (publish) => {
        if (!isProjectManager || saving) return;
        if (!title.trim()) { toast.error("Title is required."); return; }
        if (!message.trim()) { toast.error("Message is required."); return; }
        if (startAt && endAt && new Date(startAt) >= new Date(endAt)) {
            toast.error("End time must be after start time.");
            return;
        }
        executeSave(publish);
    };

    return (
        <MaintenancePageShell
            title="Maintenance Banner"
            subtitle="Show a site-wide banner to notify users of planned maintenance."
            loading={loading}
            isProjectManager={isProjectManager}
            saving={saving}
            onSaveDraft={() => handleSave(false)}
            onPublish={() => handleSave(true)}
            homeLink={homeLink}
        >
            <ContentGrid>
                <EditorPanel>
                    <FieldGroup>
                        <Label htmlFor="banner-title">Title</Label>
                        <Input
                            id="banner-title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Scheduled Maintenance"
                        />
                    </FieldGroup>

                    <FieldGroup>
                        <Label htmlFor="banner-message">Message</Label>
                        <Textarea
                            id="banner-message"
                            rows={4}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="TeamCAST will be unavailable on May 10 from 2:00 AM to 4:00 AM EST."
                        />
                    </FieldGroup>

                    <TimeRow>
                        <FieldGroup>
                            <Label htmlFor="banner-start">Start time (optional)</Label>
                            <Input
                                id="banner-start"
                                type="datetime-local"
                                value={startAt}
                                onChange={(e) => setStartAt(e.target.value)}
                            />
                            <Hint>Banner will not show before this time.</Hint>
                        </FieldGroup>

                        <FieldGroup>
                            <Label htmlFor="banner-end">End time (optional)</Label>
                            <Input
                                id="banner-end"
                                type="datetime-local"
                                value={endAt}
                                onChange={(e) => setEndAt(e.target.value)}
                            />
                            <Hint>Banner will hide after this time.</Hint>
                        </FieldGroup>
                    </TimeRow>
                </EditorPanel>

                <PreviewPanel>
                    <PreviewHeader>
                        <FiEye size={18} />
                        <span>Preview</span>
                    </PreviewHeader>
                    <PreviewCard>
                        <PreviewBanner>
                            <PreviewBannerInner>
                                <FaInfoCircle size={16} color="#3b82f6" style={{ flexShrink: 0 }} />
                                <PreviewText>
                                    <strong>{title || "Title"}: </strong>
                                    {message || "Your banner message will appear here."}
                                </PreviewText>
                            </PreviewBannerInner>
                        </PreviewBanner>
                    </PreviewCard>
                    {(startAt || endAt) && (
                        <ScheduleNote>
                            {startAt && <span>Shows from: {new Date(startAt).toLocaleString()}</span>}
                            {endAt && <span>Hides after: {new Date(endAt).toLocaleString()}</span>}
                        </ScheduleNote>
                    )}
                </PreviewPanel>
            </ContentGrid>
        </MaintenancePageShell>
    );
}

const TimeRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const PreviewCard = styled.div`
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 18px 40px rgba(15, 23, 42, 0.12);
`;

const PreviewBanner = styled.div`
    background: #eff6ff;
    border-bottom: 1px solid #dbeafe;
`;

const PreviewBannerInner = styled.div`
    padding: 10px 14px;
    display: flex;
    align-items: center;
    gap: 10px;
`;

const PreviewText = styled.p`
    margin: 0;
    font-size: 0.86rem;
    font-weight: 500;
    color: #1e3a8a;
    line-height: 1.5;
`;


const ScheduleNote = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 12px;
    padding: 10px 12px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    color: #6b7280;
    font-size: 0.82rem;
`;
